import { FaSolidCircle } from 'solid-icons/fa';
import { FiCheck } from 'solid-icons/fi';
import type { VoidComponent} from 'solid-js';
import { For, Match, Show, Switch, createMemo } from 'solid-js';
import { Text } from '../typography/index.ts';
import c from './Stepper.module.css';

type StepData = {
    valid: boolean;
    disabled?: boolean;
    label?: string;
};

export type Steps<Step extends string> = Record<Step, StepData>;

type Props<Step extends string> = {
    steps: Steps<Step>;
    currentStep: Step;
    onChangeStep: (s: Step) => void;
};

export const Stepper: VoidComponent<Props<string>> = p => {
    const steps = createMemo(() => {
        const entries = Object.entries(p.steps);
        return entries.map(([key, current], index) => {
            const previous: StepData = entries[index - 1]?.[1];
            const next = entries[index + 1]?.[1];
            return { key, current, previous, next };
        });
    });

    return (
        <div class={c.container}>
            <For each={steps()}>
                {step => (
                    <div class={c.step}>
                        <Show when={step.previous}>
                            {previous => (
                                <div
                                    class={[c.thread, c.left].join(' ')}
                                    classList={{
                                        [c.disabled]: step.current.disabled || previous().disabled,
                                        [c.valid]: previous().valid && step.current.valid,
                                    }}
                                />
                            )}
                        </Show>
                        <button
                            type='button'
                            class={c.indicator}
                            onClick={() => p.onChangeStep(step.key)}
                            disabled={step.current.disabled}
                            classList={{
                                [c.disabled]: step.current.disabled,
                                [c.valid]: step.current.valid,
                                [c.active]: step.key === p.currentStep,
                            }}
                        >
                            <Switch>
                                <Match when={step.key === p.currentStep}>
                                    <FaSolidCircle size={14} />
                                </Match>
                                <Match when={step.current.valid}>
                                    <FiCheck size={18} />
                                </Match>
                            </Switch>
                        </button>
                        <Show when={step.next}>
                            {next => (
                                <div
                                    class={[c.thread, c.right].join(' ')}
                                    classList={{
                                        [c.disabled]: step.current.disabled || next().disabled,
                                        [c.valid]: step.current.valid && next().valid,
                                    }}
                                />
                            )}
                        </Show>
                        <Text>
                            <Show
                                when={step.current.label}
                                fallback={step.key}
                            >
                                {step.current.label}
                            </Show>
                        </Text>
                    </div>
                )}
            </For>
        </div>
    );
};
